/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';

/**
 * RGBARRY-CUSTOMIZATION (W-23789028): Consent Mode UPDATE bridge — the production consent path
 * that makes GTM actually work.
 *
 * `GtmBootstrap` (gtm-bootstrap.tsx) establishes Consent Mode `default = denied` before gtm.js and
 * loads GTM, but nothing grants storage in production (`qaConsentGranted` is a QA-only override), so
 * without a bridge GA4/Ads receive nothing. This component reads the shipped binary SFCC tracking
 * consent (`useTrackingConsent` → Accepted/Declined, persisted in the SLAS token) and translates it
 * into `gtag('consent','update', …)` so Google tags unblock the moment the shopper accepts.
 *
 * It renders no DOM — it only pushes consent updates to the dataLayer `GtmBootstrap` initialized.
 *
 * ⚠️ INTERIM / BINARY BY DESIGN. The built-in banner is all-or-nothing, so Accept grants every
 * Consent Mode v2 signal and Decline denies every one. This unblocks GTM for production launch today.
 *
 * TODO(W-23789028, TrueVault consent rework): when TrueVault/Polaris is adopted it becomes the CMP
 * and supplies granular per-category updates (see docs/TRUEVAULT-CONSENT-MODE-PLAN.md +
 * src/analytics/truevault-consent.tsx). At that point `analytics.trackingConsent.enabled` is switched
 * OFF (TrueVault owns the banner), which makes `useTrackingConsent().trackingConsent` undefined here,
 * so THIS binary bridge automatically stands down and the TrueVault bridge takes over. The two are
 * mutually exclusive via the same `trackingConsent.enabled` flag — no code change needed to swap.
 */
export function GtmConsentBridge(): null {
    const config = useConfig();
    const { trackingConsent, isTrackingConsentEnabled } = useTrackingConsent();

    // Only bridge when GTM is actually loaded on this environment (containerId env-gated in
    // config.server.ts, same gate GtmBootstrap uses). Otherwise the pushes go to a dataLayer no GTM
    // reads — harmless, but skip them.
    const gtmEnabled = Boolean(config.engagement?.gtm?.containerId?.trim());

    useEffect(() => {
        if (!gtmEnabled || !isTrackingConsentEnabled) return;
        // No response yet → leave Consent Mode at GtmBootstrap's deny-by-default. Do not emit an
        // update, so cookieless pings / default behavior stay in effect until the shopper chooses.
        if (trackingConsent === undefined) return;

        const state = trackingConsent === TrackingConsent.Accepted ? 'granted' : 'denied';

        // Push via a gtag shim that forwards the `arguments` object — GTM only recognizes consent
        // commands pushed as an arguments object, NOT as a plain array. dataLayer was initialized by
        // GtmBootstrap in <head>; guard anyway in case the loader was not rendered. The global
        // `Window.dataLayer` type (Record<string, unknown>[]) is declared in ga4-datalayer.ts; the
        // arguments object is pushed via a cast, matching GtmBootstrap's inline gtag shim.
        const dataLayer = (window.dataLayer = window.dataLayer ?? []);
        function gtag(..._args: unknown[]) {
            // eslint-disable-next-line prefer-rest-params
            dataLayer.push(arguments as unknown as Record<string, unknown>);
        }
        // Mirror the four Consent Mode v2 signals GtmBootstrap defaults, so update and default always
        // cover the same set.
        gtag('consent', 'update', {
            analytics_storage: state,
            ad_storage: state,
            ad_user_data: state,
            ad_personalization: state,
        });
    }, [gtmEnabled, isTrackingConsentEnabled, trackingConsent]);

    return null;
}
